import { useState, useEffect } from 'react';
import { useDeviceOS } from './useDeviceOS';
import { useAuth } from '../AuthProvider';

export const useInstallPrompt = () => {
  const { isAndroid, isIOS } = useDeviceOS();
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);

  // Reset dismissal flag on login (when user changes)
  useEffect(() => {
    if (user) {
      localStorage.removeItem('installPromptDismissed');
      setInstallDismissed(false);
    }
  }, [user]);

  useEffect(() => {
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed === 'true') {
      setInstallDismissed(true);
      return;
    }

    // For Android: Listen for beforeinstallprompt event
    if (isAndroid) {
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowAndroidPrompt(true);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    // For iOS: Show guide banner
    if (isIOS && !dismissed) {
      // Delay showing iOS guide by 3 seconds to not be too intrusive
      const timer = setTimeout(() => {
        setShowIOSGuide(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isAndroid, isIOS]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('App installed successfully');
      } else {
        console.log('App installation dismissed');
      }

      setDeferredPrompt(null);
      setShowAndroidPrompt(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('installPromptDismissed', 'true');
    setInstallDismissed(true);
    setShowAndroidPrompt(false);
    setShowIOSGuide(false);
  };

  return {
    showAndroidPrompt,
    showIOSGuide,
    installDismissed,
    handleInstallClick,
    handleDismiss,
    isAndroid,
    isIOS,
  };
};
